/**
 * Type definitions for browser profiles.
 *
 * All types use camelCase for SDK ergonomics.
 * API responses are normalized from snake_case to camelCase.
 */

/**
 * A browser profile that stores login state (cookies, localStorage).
 *
 * @example
 * ```typescript
 * const profile = await morph.browser.profiles.getProfile('profile-id');
 * console.log(profile.cookieDomains); // ['linkedin.com', 'google.com']
 * ```
 */
export interface Profile {
  /** Unique profile identifier (UUID) */
  id: string;
  /** Human-readable profile name */
  name: string;
  /** Repository ID this profile is associated with */
  repoId: string;
  /** List of domains with stored cookies */
  cookieDomains?: string[];
  /** ISO 8601 timestamp of last use */
  lastUsedAt?: string;
  /** ISO 8601 timestamp of creation */
  createdAt: string;
  /** ISO 8601 timestamp of last update */
  updatedAt: string;
}

/**
 * Input for creating a new profile.
 *
 * @example
 * ```typescript
 * await morph.browser.profiles.createProfile({
 *   name: 'LinkedIn Production',
 *   repoId: 'owner/repo'
 * });
 * ```
 */
export interface CreateProfileInput {
  /** Profile name (1-100 characters) */
  name: string;
  /** Repository ID - profiles are repo-specific */
  repoId: string;
}

/**
 * Input for updating a profile.
 */
// Intentionally no UpdateProfileInput: update means "open a live session"

/**
 * Response for listing profiles.
 */
export interface ProfileListResponse {
  profiles: Profile[];
}

/**
 * A browser session for profile setup.
 *
 * @example
 * ```typescript
 * const session = await morph.browser.profiles.startSession();
 * console.log('Sign in at:', session.debugUrl);
 * // After user signs in...
 * await morph.browser.profiles.saveSession(session.sessionId, profile.id);
 * ```
 */
export interface ProfileSession {
  /** Unique session identifier */
  sessionId: string;
  /** Live session URL for viewing/interacting with the browser */
  debugUrl: string;
}

/**
 * Profile setup handle returned by create/update.
 *
 * Provides a live URL for login and an explicit save() helper.
 */
export interface ProfileSetup {
  profile: Profile;
  session: ProfileSession;
  save: () => Promise<Profile>;
}
/**
 * Input for starting a profile session.
 */
export interface ProfileSessionInput {
  /** Optional profile ID to update an existing profile */
  profileId?: string;
}

/**
 * Input for saving a profile session.
 */
export interface SaveProfileSessionInput {
  /** The browser session ID to extract state from */
  sessionId: string;
  /** The profile ID to save state to */
  profileId: string;
}

/**
 * Response for profile state URL.
 */
export interface ProfileStateResponse {
  /** Profile ID */
  profileId: string;
  /** Presigned URL to download the profile state (expires in `expiresIn` seconds) */
  stateUrl: string;
  /** URL expiry time in seconds */
  expiresIn: number;
}

/**
 * Repo summary for profile discovery.
 */
export interface RepoSummary {
  repoId: string;
  repoFullName?: string;
  profileCount: number;
}

/**
 * Response for repo discovery.
 */
export interface RepoListResponse {
  repos: RepoSummary[];
}

/**
 * Profile with convenience methods attached.
 *
 * @example
 * ```typescript
 * const profile = await morph.browser.profiles.getProfile('id');
 * const state = await profile.getState();
 * await profile.delete();
 * ```
 */
export interface ProfileWithMethods extends Profile {
  /** Get presigned URL for profile state */
  getState: () => Promise<ProfileStateResponse>;
  /** Delete this profile */
  delete: () => Promise<void>;
}

// ============================================================================
// Internal API types (snake_case - matches backend response)
// ============================================================================

/** @internal API response format */
export interface APIProfile {
  id: string;
  name: string;
  repo_id: string;
  cookie_domains?: string[];
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

/** @internal API response format */
export interface APIProfileSession {
  session_id: string;
  debug_url?: string;
}

/** @internal API response format */
export interface APIProfileStateResponse {
  profile_id: string;
  state_url: string;
  expires_in: number;
}

// ============================================================================
// Transformers
// ============================================================================

/**
 * Transform API profile response to SDK format.
 * @internal
 */
export function transformProfile(api: APIProfile): Profile {
  return {
    id: api.id,
    name: api.name,
    repoId: api.repo_id,
    cookieDomains: api.cookie_domains,
    lastUsedAt: api.last_used_at,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/**
 * Transform SDK input to API format.
 * @internal
 */
export function transformCreateInput(input: CreateProfileInput): { name: string; repo_id: string } {
  return {
    name: input.name,
    repo_id: input.repoId,
  };
}

/**
 * Transform API session response to SDK format.
 * @internal
 */
export function transformSession(api: APIProfileSession): ProfileSession {
  return {
    sessionId: api.session_id,
    debugUrl: api.debug_url || '',
  };
}

/**
 * Transform SDK save input to API format.
 * @internal
 */
export function transformSaveInput(input: SaveProfileSessionInput): { session_id: string; profile_id: string } {
  return {
    session_id: input.sessionId,
    profile_id: input.profileId,
  };
}

/**
 * Transform API state response to SDK format.
 * @internal
 */
export function transformStateResponse(api: APIProfileStateResponse): ProfileStateResponse {
  return {
    profileId: api.profile_id,
    stateUrl: api.state_url,
    expiresIn: api.expires_in,
  };
}
